import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, type DrawerProps, Grid, IconButton, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Modal } from "Components/_Shared/BlModal";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type FinancialAssetFormType, getFinancialAssetFormSchema } from "Forms/Schemas/Assets/financialAssetFormSchema";
import {
  getGetAssetsOverviewQueryKey,
  getGetFinancialAssetItemQueryKey,
  getGetFinancialProductExpenseQueryKey,
  getListFinancialAssetItemsQueryKey,
  useCreateFinancialAssetItem,
  useDeleteAssetItem,
  useGetAnalysisClients,
  useGetFinancialAssetItem,
  useGetFinancialProductExpense,
  useUpdateFinancialAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type FinancialAssetItem, FinancialAssetItemFinancialSubType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { FINANCIAL_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly financialAsset?: FinancialAssetItem;
} & DrawerProps;

const StyledModalContent = styled(Box)`
  width: ${(props) => props.theme.spacing(150)};
  border-radius: ${(props) => props.theme.spacing(Radius.XXXL)};
  padding: ${(props) => props.theme.spacing(Spacing.XXL)};
  background: ${(props) => props.theme.colors.white};
`;

export const FinancialAssetDrawer: FC<Props> = ({ onClose, open, financialAsset, ...rest }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const queryClient = useQueryClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: financialAssetDetail } = useGetFinancialAssetItem(analysisId, versionId, financialAsset?.guid || "", {
    query: { enabled: !!financialAsset?.guid },
  });
  const { data: financialProductExpense } = useGetFinancialProductExpense(analysisId, versionId);
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListFinancialAssetItemsQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getGetFinancialProductExpenseQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateFinancialAssetItem();
  const { mutate: update } = useUpdateFinancialAssetItem();
  const { mutate: deleteItem } = useDeleteAssetItem();

  const methods = useForm<FinancialAssetFormType>({
    resolver: yupResolver(getFinancialAssetFormSchema(t)),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = methods;

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    const defaultValues: Partial<FinancialAssetFormType> = {
      financialAssetType: financialAssetDetail?.financialSubType,
      name: financialAssetDetail?.name,
      value: financialAssetDetail?.amount.value,
      note: financialAssetDetail?.note ?? undefined,
      institution: financialAssetDetail?.institution ?? undefined,
      clientId: financialAssetDetail?.clientGuid ?? undefined,
      expenseItemId: financialAssetDetail?.relatedExpenseItemGuid ?? undefined,
      isUsableForGoals: financialAssetDetail?.isUsableForGoals === undefined ? true : financialAssetDetail?.isUsableForGoals,
    };
    reset(defaultValues);
  }, [financialAssetDetail, open, reset]);

  const financialAssetOptions: Option[] = (
    Object.keys(FinancialAssetItemFinancialSubType) as Array<FinancialAssetItemFinancialSubType>
  ).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Financial.FinancialAssetType[FINANCIAL_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const financialExpenseOptions: Option[] = [
    { value: "", name: "-" },
    ...(financialProductExpense?.items.map((expense) => ({
      value: expense.guid || "",
      name: `${expense.name}${expense.institution ? ` - ${expense.institution}` : ""} - ${formatCurrency(expense.amount.value, expense.amount.currencyCode)}/${t(Resources.Common.Month)}`,
      hidden: !(financialAssetDetail?.relatedExpenseItemGuid === expense.guid || !expense.relatedAssetItemGuid),
    })) || []),
  ];

  const clientOptions: Option[] = [
    { value: "", name: "-" },
    ...(clientsData?.clients.map((client) => ({
      value: client.guid,
      name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
    })) || []),
  ];

  const onSubmit = (data: FinancialAssetFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        institution: data.institution || "",
        financialSubType: data.financialAssetType as FinancialAssetItemFinancialSubType,
        clientGuid: data.clientId || null,
        relatedExpenseItemGuid: data.expenseItemId || null,
        isUsableForGoals: !!data.isUsableForGoals,
      };

      if (!financialAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Financial.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: financialAsset.guid || "", data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Financial.Edited));
              invalidate();
              queryClient.invalidateQueries({
                queryKey: getGetFinancialAssetItemQueryKey(analysisId, versionId, financialAsset.guid || ""),
              });
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error));
    }
  };

  const onDelete = (relatedExpenseItemGuid?: string) => {
    try {
      deleteItem(
        {
          analysisId,
          versionId,
          itemId: financialAsset?.guid || "",
          params: relatedExpenseItemGuid ? { relatedExpenseItemGuid } : undefined,
        },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Financial.Deleted));
            invalidate();
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
        },
      );

      setDeleteModalOpen(false);
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error));
    }
  };

  const financialAssetType = watch("financialAssetType");

  return (
    <>
      <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
              <Typography variant="h2">
                {financialAsset ? t(Resources.AnalysisDetail.Assets.Financial.Edit) : t(Resources.AnalysisDetail.Assets.Financial.Add)}
              </Typography>

              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DrawerContent>
              <Grid container spacing={Spacing.XL} p={Spacing.XXL}>
                <Grid size={12}>
                  <FormDropdown
                    name="financialAssetType"
                    disabled={readOnly}
                    options={financialAssetOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.Assets.Financial.FinancialAssetType.Title)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="name"
                    label={t(Resources.AnalysisDetail.Assets.Name)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="value"
                    label={t(Resources.AnalysisDetail.Assets.Financial.Value)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                    unmask="typed"
                    placeholder="0"
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        {t(Resources.Common.CZKSuffix)}
                      </Typography>
                    }
                  />
                </Grid>
                {financialProductExpense?.entryMode === "individual" &&
                  !([FinancialAssetItemFinancialSubType.cash, FinancialAssetItemFinancialSubType.bank_account] as string[]).includes(
                    financialAssetType,
                  ) && (
                    <Grid size={12}>
                      <FormDropdown
                        name="expenseItemId"
                        disabled={readOnly}
                        options={financialExpenseOptions}
                        control={control}
                        label={t(Resources.AnalysisDetail.Assets.Financial.RegisteredExpenses)}
                        errors={errors}
                      />
                    </Grid>
                  )}

                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="institution"
                    label={t(Resources.AnalysisDetail.Assets.Financial.Insitution)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <FormDropdown
                    name="clientId"
                    control={control}
                    disabled={readOnly}
                    options={clientOptions}
                    label={t(Resources.AnalysisDetail.Assets.Financial.BelongsTo)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={12}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                    name="note"
                    errors={errors}
                    multiline
                    minRows={6}
                    maxRows={8}
                    characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                  />
                </Grid>
                <Grid size={12}>
                  <Divider />
                </Grid>
                <Grid size={12}>
                  <Typography fontWeight={600} variant="subtitle1" mb={Spacing.XL}>
                    {t(Resources.AnalysisDetail.Assets.Attributes)}
                  </Typography>
                  <BlCheckboxInput
                    control={control}
                    disabled={readOnly}
                    name="isUsableForGoals"
                    text={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </DrawerContent>

            <Box display="flex" alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
              <Box>
                {!!financialAsset && (
                  <Button
                    variant="text"
                    color="error"
                    disabled={readOnly}
                    sx={{
                      marginRight: Spacing.XL,
                    }}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    {t(Resources.Common.Delete)}
                  </Button>
                )}
              </Box>

              <Box display="flex" flexDirection="row" justifyContent="flex-end">
                <Button
                  onClick={onClose}
                  color="secondary"
                  sx={{
                    marginRight: Spacing.XL,
                  }}
                >
                  {t(Resources.Common.Close)}
                </Button>
                <Button type="submit" disabled={readOnly}>
                  {financialAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
                </Button>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Drawer>
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <StyledModalContent sx={{ boxShadow: 3 }}>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Typography variant="h2">{t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}</Typography>
            </Box>

            <Typography variant="body1">
              {t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteLoanExpenseItemWithoutAssetHint)}
            </Typography>

            <Box display="flex" justifyContent="space-between">
              <Box>
                <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                  {t(Resources.Common.Close)}
                </Button>
              </Box>
              <Box display="flex" gap={Spacing.L}>
                {financialAssetDetail?.relatedExpenseItemGuid ? (
                  <>
                    <Button disabled={readOnly} onClick={() => onDelete(financialAssetDetail.relatedExpenseItemGuid ?? undefined)}>
                      {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithLoanExpense)}
                    </Button>

                    <Button disabled={readOnly} onClick={() => onDelete()}>
                      {t(Resources.AnalysisDetail.Assets.Liabilities.DeleteLiabilityWithoutLoanExpense)}
                    </Button>
                  </>
                ) : (
                  <Button disabled={readOnly} onClick={() => onDelete()}>
                    {t(Resources.Common.Yes)}
                  </Button>
                )}
              </Box>
            </Box>
          </Stack>
        </StyledModalContent>
      </Modal>
    </>
  );
};
